import React from 'react';
import { MenuSection, MenuSectionHeading, MenuList } from '@atlaskit/navigation-system';
import { fg } from '@atlassian/jira-feature-gating';
import { ForgeScreenEvent } from '@atlassian/jira-forge-ui-analytics/src/common/ui/index.tsx';
import { TriggerAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/admin-page/index.tsx';
import { ADMIN_PAGE_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type { AdminPage } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { parseExtensionId } from '@atlassian/jira-forge-ui-utils-internal/src/utils/parse-extension-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { toSections } from '@atlassian/jira-navigation-apps-sidebar-forge/src/common/utils/subpages/index.tsx';
import { useAdminForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/admin/index.tsx';
import type { MenuItemAnalyticsWithoutItemId } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/utils/get-analytics-attributes/types.tsx';
import { ForgeItem } from '../../common/ui/forge-item/index.tsx';
import messages from './messages.tsx';

export const getItemHref = (extension: AdminPage) => {
	const { appId, envId } = parseExtensionId(extension.id);
	return `/jira/settings/apps/${appId}/${envId}`;
};

type Props = {
	analytics: MenuItemAnalyticsWithoutItemId;
};

export const ForgeAdminSection = ({ analytics }: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { data } = useAdminForgeApps();
	const pages: AdminPage[] | null = data?.adminPage ?? null;

	if (!pages?.length) return null;

	return (
		<TriggerAnalyticsWrapper>
			<MenuSection isMenuListItem={fg('platform_design_system_nav4_sidenav_a11y')}>
				<MenuSectionHeading>{formatMessage(messages.heading)}</MenuSectionHeading>
				<MenuList>
					{pages.map((extension) => (
						<ForgeItem
							key={extension.id}
							id={extension.id}
							environmentType={extension.environmentType}
							environmentKey={extension.environmentKey}
							url={getItemHref(extension)}
							name={extension.properties.title}
							sections={toSections(extension)}
							iconUrl={extension.properties.icon}
							analytics={analytics}
							showIcon
						/>
					))}
				</MenuList>
			</MenuSection>
			<ForgeScreenEvent
				attributes={{
					moduleType: ADMIN_PAGE_MODULE,
				}}
			/>
		</TriggerAnalyticsWrapper>
	);
};
